import { useState } from 'react';
import { Wallet, TrendingUp, TrendingDown } from 'lucide-react';
import { AssetsTab } from './AssetsTab';
import { DebtTab } from './DebtTab';
import { Asset, Debt } from '../lib/types';
import { cn, formatCurrency } from '../lib/utils';

interface WealthTabProps {
  initialView?: 'assets' | 'debts';
  assets: Asset[];
  debts: Debt[];
  localCurrency: string;
  onManageAssets: () => void;
  onEditAsset: (asset: Asset) => void;
  onDeleteAsset: (id: string) => void;
  onDeleteDebt: (id: string) => void;
  onManageDebts: () => void;
  onMakePayment: (debt: Debt) => void;
  isPrivacyMode: boolean;
}

export function WealthTab({ 
  initialView = 'assets',
  assets, 
  debts, 
  localCurrency, 
  onManageAssets, 
  onEditAsset, 
  onDeleteAsset, 
  onManageDebts, 
  onDeleteDebt,
  onMakePayment, 
  isPrivacyMode 
}: WealthTabProps) {
  const [view, setView] = useState<'assets' | 'debts'>(initialView);

  const totalAssets = assets.reduce((sum, a) => sum + a.value, 0);
  const totalDebts = debts.reduce((sum, d) => sum + d.currentBalance, 0);
  const netWorth = totalAssets - totalDebts;

  return (
    <div className="space-y-6">
      {/* Wealth Header & Net Worth */}
      <div className="space-y-6">
        <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 px-1">Wealth Dashboard</h2>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-zinc-900 dark:bg-zinc-100 p-5 rounded-[24px] shadow-lg shadow-zinc-200 dark:shadow-zinc-900/50 flex flex-col justify-between relative overflow-hidden group">
            <div className="relative z-10">
              <div className="w-10 h-10 rounded-2xl bg-white/10 dark:bg-black/10 flex items-center justify-center text-white dark:text-black mb-4">
                <Wallet className="w-5 h-5" />
              </div>
              <p className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest">Net Worth</p>
              <p className={cn("text-2xl font-bold text-white dark:text-black mt-1", isPrivacyMode && "blur-md select-none")}>
                {formatCurrency(netWorth, localCurrency)}
              </p>
            </div>
            {/* Decorative background blob */}
            <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-white/5 dark:bg-black/5 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
          </div>

          <button onClick={() => setView('assets')} className={cn("text-left p-5 rounded-[24px] border transition-all duration-300 relative overflow-hidden group", view === 'assets' ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 ring-2 ring-emerald-500/20" : "bg-white dark:bg-zinc-900 border-zinc-100 dark:border-zinc-800 hover:border-emerald-200 dark:hover:border-emerald-800")}>
            <div className="w-10 h-10 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mb-4">
              <TrendingUp className="w-5 h-5" />
            </div>
            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Total Assets</p>
            <p className={cn("text-lg font-bold text-zinc-900 dark:text-zinc-100", isPrivacyMode && "blur-sm select-none")}>
              {formatCurrency(totalAssets, localCurrency)}
            </p>
          </button>

          <button onClick={() => setView('debts')} className={cn("text-left p-5 rounded-[24px] border transition-all duration-300 relative overflow-hidden group", view === 'debts' ? "bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800 ring-2 ring-rose-500/20" : "bg-white dark:bg-zinc-900 border-zinc-100 dark:border-zinc-800 hover:border-rose-200 dark:hover:border-rose-800")}>
            <div className="w-10 h-10 rounded-2xl bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center text-rose-600 dark:text-rose-400 mb-4">
              <TrendingDown className="w-5 h-5" />
            </div>
            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Total Liabilities</p>
            <p className={cn("text-lg font-bold text-zinc-900 dark:text-zinc-100", isPrivacyMode && "blur-sm select-none")}>
              {formatCurrency(totalDebts, localCurrency)}
            </p>
          </button>
        </div>
      </div>

      {/* Segmented Control */}
      <div className="flex p-1 bg-zinc-100 dark:bg-zinc-900 rounded-xl">
        <button
          onClick={() => setView('assets')}
          className={cn(
            "flex-1 py-2.5 text-xs font-bold uppercase tracking-wider rounded-lg transition-all",
            view === 'assets' 
              ? "bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 shadow-sm" 
              : "text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
          )}
        >
          Assets
        </button>
        <button
          onClick={() => setView('debts')}
          className={cn(
            "flex-1 py-2.5 text-xs font-bold uppercase tracking-wider rounded-lg transition-all",
            view === 'debts' 
              ? "bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 shadow-sm" 
              : "text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
          )}
        >
          Debts
        </button>
      </div>

      {/* Content Area */}
      <div className="min-h-[400px]">
        {view === 'assets' ? (
          <AssetsTab
            assets={assets}
            localCurrency={localCurrency}
            onManageAssets={onManageAssets}
            onEditAsset={onEditAsset}
            onDeleteAsset={onDeleteAsset}
            isPrivacyMode={isPrivacyMode}
          />
        ) : (
          <DebtTab
            debts={debts}
            localCurrency={localCurrency}
            onManageDebts={onManageDebts}
            onMakePayment={onMakePayment}
            onDeleteDebt={onDeleteDebt}
            isPrivacyMode={isPrivacyMode}
          />
        )}
      </div>
    </div>
  );
}
